import { useToast } from '@monorepo/shared/contexts';
import { FormTemplateResponse } from 'mapistry-shared';
import { useCallback, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFormTemplateGeneratedFromPdfMessageHandlers } from '../../../../../vortex/src/contexts/pushMessages/pushMessageHandlers/useFormTemplateGeneratedFromPdfMessageHandlers';
import { useStartGeneratingFormTemplateFromPdf } from './useStartGeneratingFormTemplateFromPdf';

export function useGenerateFormTemplateFromPdfAndListenForPushMessageResponse() {
  const { showUserFriendlyErrorToast, error: showErrorToast } = useToast();

  const [tabIdentifier, setTabIdentifier] = useState<string>();
  const [formTemplate, setFormTemplate] = useState<FormTemplateResponse>();
  const [startedProcessingAt, setStartedProcessingAt] = useState<Date>();
  const isProcessingImage = !!startedProcessingAt;

  const [startGeneratingFormTemplateFromPdf] =
    useStartGeneratingFormTemplateFromPdf({
      config: {
        onError: (e) => {
          setStartedProcessingAt(undefined);
          setTabIdentifier(undefined);
          showUserFriendlyErrorToast(e, undefined, { dontAutoHide: true });
        },
      },
    });

  const handleUploadFormImage = useCallback(
    ({ file }: { file?: File }) => {
      if (!file) return;

      const uniqueId = uuidv4();
      setStartedProcessingAt(new Date());
      setTabIdentifier(uniqueId);
      void startGeneratingFormTemplateFromPdf({
        file,
        tabIdentifier: uniqueId,
      });
    },
    [startGeneratingFormTemplateFromPdf],
  );

  const onFinishedGeneratingFormTemplateFromPdf = useCallback(
    ({
      formTemplate: generatedFormTemplate,
      error: errorMessage,
      tabIdentifier: tabIdentifierFromMessage,
    }: {
      formTemplate?: FormTemplateResponse;
      error?: string;
      tabIdentifier: string;
    }) => {
      if (tabIdentifierFromMessage !== tabIdentifier) return;

      setStartedProcessingAt(undefined);
      setTabIdentifier(undefined);

      if (generatedFormTemplate) setFormTemplate(generatedFormTemplate);
      if (errorMessage) {
        // eslint-disable-next-line no-console
        console.log('Form template creation error', errorMessage);
        showErrorToast(
          `Something went wrong processing that file. Try again. ${errorMessage} `,
          { dontAutoHide: true },
        );
      }
    },
    [showErrorToast, tabIdentifier],
  );

  // start listening to push notifications for form template generation, and pass in callback on success
  useFormTemplateGeneratedFromPdfMessageHandlers(
    onFinishedGeneratingFormTemplateFromPdf,
  );

  return {
    processedFormTemplate: formTemplate,
    isProcessingImage,
    handleUploadFormImage,
    startedProcessingAt,
  };
}
